import { LoadState } from "../../constants/enums";

// ---------------------------------------------------------------------------
// Enums / allowed value sets (mirror backend Joi enums)
// ---------------------------------------------------------------------------

export const PBS_QUOTE_PROFILES = ["Personal", "Commercial"] as const;
export const PBS_REGULAR_CONTINUOUS_PAY = ["Regular", "Continuous Pay"] as const;
export const PBS_QUOTE_STATUSES = ["Full", "Pending Submission"] as const;
export const PBS_IS_RENEWAL = ["New", "Renewal"] as const;
export const PBS_ACCOUNT_TYPES = ["Checking", "Savings", ""] as const;
export const PBS_PREFERRED_LANGUAGES = ["English", "Francais"] as const;
export const PBS_BILLING_METHODS = ["Invoice", "ACH"] as const;

export type PbsQuoteProfile = (typeof PBS_QUOTE_PROFILES)[number];
export type PbsRegularContinuousPay = (typeof PBS_REGULAR_CONTINUOUS_PAY)[number];
export type PbsQuoteStatus = (typeof PBS_QUOTE_STATUSES)[number];
export type PbsIsRenewal = (typeof PBS_IS_RENEWAL)[number];
export type PbsAccountType = (typeof PBS_ACCOUNT_TYPES)[number];
export type PbsPreferredLanguage = (typeof PBS_PREFERRED_LANGUAGES)[number];
export type PbsBillingMethod = (typeof PBS_BILLING_METHODS)[number];

export type PbsQuoteMode = "calculate" | "create" | "renewal";

// Module key used to guard the PBS Quotes routes (matches security module_key)
export const PBS_QUOTES_MODULE_KEY = "PBS_QUOTES";

// ---------------------------------------------------------------------------
// Request payload shapes (doc §2.2.1 - §2.2.3)
// ---------------------------------------------------------------------------

export interface IPbsCustomerInfo {
  Quoting_For: string;
  Name_1: string;
  Name_2: string;
  Name_3: string;
  Contact_Name: string;
  Address_Line_1: string;
  Address_Line_2: string;
  Address_Line_3: string;
  City: string;
  Region: string; // 2-char province/state code
  Postal_Code: string;
  Additional_Address_Line_1: string;
  Additional_Address_Line_2: string;
  Additional_Address_Line_3: string;
  Additional_City: string;
  Additional_Region: string;
  Additional_Postal_Code: string;
  "E-Mail": string;
  Main_Phone: string;
  Home_Phone: string;
  Main_Fax: string;
  Agent_Code: string;
  Quote_Status: PbsQuoteStatus | "";
  Origination_Code: string;
  Quote_Profile: PbsQuoteProfile | "";
  Quote_Configuration: string;
  Is_Renewal: PbsIsRenewal | "";
  Bank_Name: string;
  Routing_Number: string;
  Account_Number: string;
  Account_Type: PbsAccountType;
  Pricing_Program: string;
  Customer_ID: string;
  Old_Account_Number: string;
  Regular_Continuous_Pay: PbsRegularContinuousPay | "";
  Broker_Contact: string;
  Preferred_Language: PbsPreferredLanguage | "";
}

export interface IPbsPolicy {
  Policy_Number: string;
  Coverage_Code: string;
  Effective_Date: string; // YYYY-MM-DD
  Policy_Term: string;
  Days_To_Cancel: string;
  Carrier_Code: string;
  Carrier_External_ID: string;
  GA_Code: string;
  GA_External_ID: string;
  Premium: string;
  Earned_Taxes_Fees: string;
  Financed_Taxes_Fees: string;
  Minimum_Earned_Premium: string;
  Minimum_Earned_Premium_Percent: string;
}

export interface IPbsTerms {
  Billing_Method: PbsBillingMethod | "";
  Earned_Broker_Fee: string;
  Financed_Broker_Fee: string;
  Down_Payment_Amount: string;
  Down_Payment_Percent: string;
  Number_Of_Installments: string;
  First_Due_Date: string; // YYYY-MM-DD
}

// Composite payloads sent to the three endpoints
export interface ICalculatePbsQuotePayload {
  customer: IPbsCustomerInfo;
  policies: IPbsPolicy[];
  terms: IPbsTerms;
}

export interface ICreatePbsQuotePayload extends ICalculatePbsQuotePayload {
  returnPfa: boolean;
}

export interface IRenewalPbsQuotePayload extends ICreatePbsQuotePayload {
  oldAccountNumber: string;
}

// ---------------------------------------------------------------------------
// Response shapes (backend `data` block)
// ---------------------------------------------------------------------------

export interface IPbsQuoteInfo {
  quoteNumber: string | null;
  totalPaymentAmount: number | null;
  totalPremium: number | null;
  downPayment: number | null;
  amountFinanced: number | null;
  financeCharge: number | null;
  nonRefundableFee: number | null;
  numberOfInstallments: number | null;
  installmentAmount: number | null;
  apr: number | null;
}

export interface IPbsPolicyEcho {
  policyNumber: string | null;
  coverageCode: string | null;
  premium: number | null;
  effectiveDate: string | null;
}

export interface IPbsOtherInfo {
  preferredLanguage: string | null;
  pricingProgram: string | null;
  firstDueDate: string | null;
  eSignatureUrl: string | null;
  newRenewal: string | null;
  applicationFee: number | null;
  policies: IPbsPolicyEcho[];
}

export interface IPbsQuoteResult {
  quoteNumber: string | null;
  quote: IPbsQuoteInfo | null;
  otherInfo: IPbsOtherInfo | null;
  eSignatureUrl: string | null;
}

// ---------------------------------------------------------------------------
// Quote History (from pbs_quote_logs)
// ---------------------------------------------------------------------------

export interface IPbsQuoteLog {
  id: number | string;
  created_at: string;
  mode: PbsQuoteMode;
  quote_number: string | null;
  result: "ok" | "errors";
  errors: string[];
  request_meta_json: {
    customer?: Partial<IPbsCustomerInfo>;
    [key: string]: unknown;
  };
  quote_info_json: IPbsQuoteInfo | null;
  duration_ms: number | null;
}

export interface IPbsQuoteHistoryFilters {
  mode: PbsQuoteMode | "all";
  from_date: string | null;
  to_date: string | null;
  quoteNumber: string;
  pageNo: number;
  itemPerPage: number;
}

// ---------------------------------------------------------------------------
// Redux state
// ---------------------------------------------------------------------------

export interface IPbsQuoteModeState {
  loading: LoadState;
  result: IPbsQuoteResult | null;
  errors: string[];
  error: string | null;
  pfaBase64: string | null;
  /** hash of the last request payload, used to mark the result "stale" */
  lastRequestHash: string | null;
}

export interface IPbsQuoteHistoryState {
  list: IPbsQuoteLog[];
  loading: LoadState;
  totalRecords: number;
  error: string | null;
  filters: IPbsQuoteHistoryFilters;
}

export interface IPbsQuoteState {
  calculate: IPbsQuoteModeState;
  create: IPbsQuoteModeState;
  renewal: IPbsQuoteModeState;
  history: IPbsQuoteHistoryState;
}

// ---------------------------------------------------------------------------
// Initial state + form factory constants
// ---------------------------------------------------------------------------

const initialModeState = (): IPbsQuoteModeState => ({
  loading: LoadState.NotLoaded,
  result: null,
  errors: [],
  error: null,
  pfaBase64: null,
  lastRequestHash: null,
});

export const initialPbsQuoteHistoryFilters: IPbsQuoteHistoryFilters = {
  mode: "all",
  from_date: null,
  to_date: null,
  quoteNumber: "",
  pageNo: 1,
  itemPerPage: 25,
};

export const initialPbsQuoteState: IPbsQuoteState = {
  calculate: initialModeState(),
  create: initialModeState(),
  renewal: initialModeState(),
  history: {
    list: [],
    loading: LoadState.NotLoaded,
    totalRecords: 0,
    error: null,
    filters: { ...initialPbsQuoteHistoryFilters },
  },
};

export const initialCustomer: IPbsCustomerInfo = {
  Quoting_For: "",
  Name_1: "",
  Name_2: "",
  Name_3: "",
  Contact_Name: "",
  Address_Line_1: "",
  Address_Line_2: "",
  Address_Line_3: "",
  City: "",
  Region: "",
  Postal_Code: "",
  Additional_Address_Line_1: "",
  Additional_Address_Line_2: "",
  Additional_Address_Line_3: "",
  Additional_City: "",
  Additional_Region: "",
  Additional_Postal_Code: "",
  "E-Mail": "",
  Main_Phone: "",
  Home_Phone: "",
  Main_Fax: "",
  Agent_Code: "",
  Quote_Status: "",
  Origination_Code: "",
  Quote_Profile: "",
  Quote_Configuration: "",
  Is_Renewal: "",
  Bank_Name: "",
  Routing_Number: "",
  Account_Number: "",
  Account_Type: "",
  Pricing_Program: "",
  Customer_ID: "",
  Old_Account_Number: "",
  Regular_Continuous_Pay: "",
  Broker_Contact: "",
  Preferred_Language: "",
};

export const initialPolicy: IPbsPolicy = {
  Policy_Number: "",
  Coverage_Code: "",
  Effective_Date: "",
  Policy_Term: "",
  Days_To_Cancel: "5",
  Carrier_Code: "",
  Carrier_External_ID: "",
  GA_Code: "",
  GA_External_ID: "",
  Premium: "",
  Earned_Taxes_Fees: "",
  Financed_Taxes_Fees: "",
  Minimum_Earned_Premium: "",
  Minimum_Earned_Premium_Percent: "",
};

export const initialTerms: IPbsTerms = {
  Billing_Method: "",
  Earned_Broker_Fee: "",
  Financed_Broker_Fee: "",
  Down_Payment_Amount: "",
  Down_Payment_Percent: "",
  Number_Of_Installments: "",
  First_Due_Date: "",
};

// Shape used by thunks when a request is rejected
export interface IPbsQuoteRejectValue {
  message: string;
  status?: number;
  errors?: string[];
}
